/**
 * 대여 신청서 (교회 신청 · 관리자 전화 접수 공용)
 *  - 수령일을 고르면 반납일이 영업일 N일째로 잡힘, 더 늦게 바꿀 수 있음
 *  - 고른 기간의 남은 수량 안에서만 신청, 택배는 택배 가능한 물품만
 */
import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Collapse, FormControlLabel, MenuItem, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { addBizDays, addDays, isOff, josa, minRemaining, offReason, OccReservation } from './logic';
import { fmtShort, RentalItem, RentalSettings, RequestBody, won } from './api';
import { formatPhoneInput } from '../btcert/common';

const PURPOSES = ['성경퀴즈대회', '게임', '그랑프리', '기타'];

type Props = {
  mode: 'church' | 'admin';
  items: RentalItem[];
  settings: RentalSettings;
  reservations: OccReservation[];
  today: string;
  church?: { regNo: string; churchName: string; managerName?: string; managerPhone?: string };
  onSubmit: (body: RequestBody & { agree: boolean; regNo?: string; churchName?: string }) => Promise<void>;
};

const firstBizDay = (today: string, holidays: RentalSettings['holidays']) => {
  let d = addDays(today, 1);
  while (isOff(d, holidays)) d = addDays(d, 1);
  return d;
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
    <Typography fontSize={13} fontWeight={800} color="warning.main" mb={1.5}>{title}</Typography>
    {children}
  </Paper>
);

const RequestForm: React.FC<Props> = ({ mode, items, settings, reservations, today, church, onSubmit }) => {
  const holidays = useMemo(() => settings.holidays || [], [settings.holidays]);
  const bizDays = settings.bizDays || 7;
  const [regNo, setRegNo] = useState(church?.regNo || '');
  const [churchName, setChurchName] = useState(church?.churchName || '');
  const [contact, setContact] = useState(church?.managerName || '');
  const [phone, setPhone] = useState(church?.managerPhone || '');
  const [qty, setQty] = useState<Record<string, number>>({});
  const [start, setStart] = useState(() => firstBizDay(today, holidays));
  const [end, setEnd] = useState(() => addBizDays(firstBizDay(today, holidays), bizDays, holidays));
  const [method, setMethod] = useState<'방문' | '택배'>('방문');
  const [time, setTime] = useState(settings.visitTimes?.[0] || '');
  const [shipName, setShipName] = useState('');
  const [shipPhone, setShipPhone] = useState('');
  const [shipAddr, setShipAddr] = useState('');
  const [purpose, setPurpose] = useState(PURPOSES[0]);
  const [memo, setMemo] = useState('');
  const [agree, setAgree] = useState(mode === 'admin');
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const minEnd = start ? addBizDays(start, bizDays, holidays) : '';
  const calc = useMemo(() => {
    const lines = items.filter((i) => (qty[i._id] || 0) > 0).map((i) => ({ item: i, qty: qty[i._id] }));
    const canShip = lines.length > 0 && lines.every((l) => l.item.delivery);
    const fee = lines.reduce((s, l) => s + l.qty * (l.item.fee || 0), 0);
    const ship = method === '택배' ? lines.reduce((s, l) => s + (l.item.deliveryFee || 0), 0) : 0;
    const problems: string[] = [];
    if (!lines.length) problems.push('대여할 물품을 1개 이상 고르세요.');
    if (start && (mode === 'admin' ? start < today : start <= today)) problems.push('수령일은 내일 이후로 고르세요.');
    if (start && isOff(start, holidays)) problems.push(`수령일 ${fmtShort(start)}은 ${josa(offReason(start, holidays) || '', '이라', '라')} 고를 수 없어요.`);
    if (end && isOff(end, holidays)) problems.push(`반납일 ${fmtShort(end)}은 ${josa(offReason(end, holidays) || '', '이라', '라')} 고를 수 없어요.`);
    if (end && minEnd && end < minEnd) problems.push(`반납일은 영업일 ${bizDays}일째인 ${fmtShort(minEnd)} 이후로 고르세요.`);
    lines.forEach((l) => {
      const left = minRemaining({ id: l.item._id, total: l.item.total, repair: l.item.repair }, start, end, reservations, today);
      if (l.qty > left) problems.push(`${josa(l.item.name, '은', '는')} 이 기간에 ${Math.max(left, 0)}개까지 빌릴 수 있어요.`);
      if (l.item.maxPerOrder && l.qty > l.item.maxPerOrder) problems.push(`${josa(l.item.name, '은', '는')} 한 번에 ${l.item.maxPerOrder}개까지예요.`);
    });
    if (method === '택배' && !canShip) problems.push('택배가 안 되는 물품이 있어요. 본부 방문을 고르세요.');
    return { lines, canShip, fee, ship, total: fee + ship, problems };
  }, [items, qty, method, start, end, minEnd, reservations, today, holidays, bizDays, mode]);

  const changeStart = (v: string) => { setStart(v); if (v) setEnd(addBizDays(v, bizDays, holidays)); setError(''); };
  const bump = (it: RentalItem, d: number) => {
    setQty((q) => ({ ...q, [it._id]: Math.max(0, (q[it._id] || 0) + d) }));
    setError('');
  };
  const submit = async () => {
    const extra: string[] = [];
    if (!contact.trim() || phone.replace(/\D/g, '').length < 9) extra.push('담당자 이름과 휴대폰을 입력하세요.');
    if (method === '택배' && (!shipName.trim() || !shipAddr.trim())) extra.push('택배 받을 분과 주소를 입력하세요.');
    if (mode === 'admin' && regNo.replace(/\D/g, '').length !== 4) extra.push('교회 등록번호 4자리를 입력하세요.');
    if (!agree) extra.push('대여 약관에 동의해 주세요.');
    const all = [...calc.problems, ...extra];
    if (all.length) { setError(all.join('\n')); return; }
    setBusy(true); setError('');
    try {
      await onSubmit({
        regNo, churchName, agree, contact: contact.trim(), phone, start, end, method, time: method === '방문' ? time : '',
        shipName, shipPhone, shipAddr, purpose, memo, lines: calc.lines.map((l) => ({ itemId: l.item._id, qty: l.qty })),
      });
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) 340px' }, gap: 2, alignItems: 'start' }}>
      <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
        <Section title={mode === 'church' ? '교회 정보 · 교회 기본 데이터에서 불러옴' : '교회 (전화 접수)'}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0,1fr))' }, gap: 1.5 }}>
            <TextField size="small" label="교회 등록번호" value={regNo} onChange={(e) => setRegNo(e.target.value.replace(/\D/g, '').slice(0, 4))}
              InputProps={{ readOnly: mode === 'church' }} inputProps={{ inputMode: 'numeric' }} />
            <TextField size="small" label="교회명" value={churchName} onChange={(e) => setChurchName(e.target.value)}
              InputProps={{ readOnly: mode === 'church' }} helperText={mode === 'admin' ? '비우면 등록번호로 찾아요' : undefined} />
            <TextField size="small" label="담당자" value={contact} onChange={(e) => setContact(e.target.value)} />
            <TextField size="small" label="휴대폰" value={phone} onChange={(e) => setPhone(formatPhoneInput(e.target.value))} inputProps={{ inputMode: 'numeric' }} />
          </Box>
        </Section>

        <Section title="대여 물품 · 고른 기간에 남은 수량">
          {!items.length && <Typography color="text.secondary" fontSize={14}>지금 빌릴 수 있는 물품이 없어요.</Typography>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(3, minmax(0,1fr))' }, gap: 1.5 }}>
            {items.map((it) => {
              const left = Math.max(0, minRemaining({ id: it._id, total: it.total, repair: it.repair }, start, end, reservations, today));
              const q = qty[it._id] || 0;
              return (
                <Paper key={it._id} variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, borderColor: q ? 'primary.main' : undefined, boxShadow: q ? (t) => `inset 0 0 0 1px ${t.palette.primary.main}` : 'none' }}>
                  <Typography fontWeight={800}>{it.name}</Typography>
                  <Typography fontSize={12} color="text.secondary">남은 {left} · {it.delivery ? '택배 가능' : '방문 수령만'} · {won(it.fee)}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                    <Button size="small" variant="outlined" sx={{ minWidth: 36, fontSize: 18, lineHeight: 1 }} onClick={() => bump(it, -1)} aria-label={`${it.name} 줄이기`}>−</Button>
                    <Typography fontWeight={800} minWidth={18} textAlign="center">{q}</Typography>
                    <Button size="small" variant="outlined" sx={{ minWidth: 36, fontSize: 18, lineHeight: 1 }} onClick={() => bump(it, 1)} disabled={q >= left} aria-label={`${it.name} 늘리기`}>+</Button>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Section>

        <Section title={`대여 기간 · 영업일 ${bizDays}일 이상`}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField size="small" type="date" label="수령일" value={start} onChange={(e) => changeStart(e.target.value)} InputLabelProps={{ shrink: true }}
              inputProps={{ min: mode === 'admin' ? today : addDays(today, 1) }} />
            <TextField size="small" type="date" label="반납일" value={end} onChange={(e) => { setEnd(e.target.value); setError(''); }} InputLabelProps={{ shrink: true }}
              inputProps={{ min: minEnd }} />
          </Box>
          <Typography fontSize={12} color="text.secondary" mt={1} mb={2}>
            수령일을 고르면 반납일이 영업일 {bizDays}일째({fmtShort(minEnd)})로 잡혀요. 더 늦은 날로 바꿀 수 있고, 토요일·주일·공휴일은 고를 수 없어요.
          </Typography>
          <Typography fontSize={12} color="text.secondary" mb={0.5}>수령 방법</Typography>
          <ToggleButtonGroup exclusive size="small" color="primary" value={method} onChange={(_, v) => v && setMethod(v)} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            <ToggleButton value="방문">본부 방문</ToggleButton>
            <ToggleButton value="택배" disabled={!calc.canShip}>택배{calc.lines.length && !calc.canShip ? ' (고른 물품 불가)' : ''}</ToggleButton>
          </ToggleButtonGroup>
          {method === '방문' ? (
            <TextField select size="small" fullWidth label="방문 시간" value={time} onChange={(e) => setTime(e.target.value)}>
              {(settings.visitTimes || []).map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <TextField size="small" label="받는 분" value={shipName} onChange={(e) => setShipName(e.target.value)} />
              <TextField size="small" label="받는 분 연락처" value={shipPhone} onChange={(e) => setShipPhone(formatPhoneInput(e.target.value))} />
              <TextField size="small" label="배송 주소" value={shipAddr} onChange={(e) => setShipAddr(e.target.value)} sx={{ gridColumn: { sm: '1 / -1' } }} />
            </Box>
          )}
        </Section>

        <Section title="사용 목적 · 요청 사항">
          <ToggleButtonGroup exclusive size="small" color="primary" value={purpose} onChange={(_, v) => v && setPurpose(v)} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            {PURPOSES.map((p) => <ToggleButton key={p} value={p}>{p}</ToggleButton>)}
          </ToggleButtonGroup>
          <TextField size="small" fullWidth label="요청 사항 (선택)" value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="예: 오전 10시에 방문 예정입니다" />
        </Section>
      </Box>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, position: { md: 'sticky' }, top: { md: 80 } }}>
        <Typography fontWeight={800} mb={1}>신청 요약</Typography>
        {calc.lines.length ? calc.lines.map((l) => (
          <Box key={l.item._id} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, py: 0.5 }}>
            <span>{l.item.name} × {l.qty}</span><span>{won(l.qty * l.item.fee)}</span>
          </Box>
        )) : <Typography fontSize={13} color="text.secondary">고른 물품이 없어요</Typography>}
        {calc.ship > 0 && <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, py: 0.5 }}><span>택배비</span><span>{won(calc.ship)}</span></Box>}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, py: 0.5 }}><span>기간</span><span>{fmtShort(start)} ~ {fmtShort(end)}</span></Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderTop: 1, borderColor: 'divider', mt: 1, pt: 1.5 }}>
          <Typography fontWeight={700}>대여료</Typography>
          <Typography fontWeight={800} fontSize={24}>{won(calc.total)}</Typography>
        </Box>
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(59,130,246,.12)' : '#eff6ff'), border: '1px solid rgba(59,130,246,.35)', fontSize: 13, lineHeight: 1.7 }}>
          <b>이벤트 계좌</b><br />{settings.account} · {settings.holder}<br />
          <Typography component="span" fontSize={12} color="text.secondary">입금자명은 교회명 · 신청 후 {Math.round((settings.payDueHours || 24) / 24)}일 이내</Typography>
        </Box>
        {mode === 'church' && (
          <>
            <FormControlLabel sx={{ mt: 1, alignItems: 'flex-start' }} control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} sx={{ pt: 0.5 }} />}
              label={<Typography fontSize={13}>대여 약관에 동의합니다 <Button size="small" onClick={(e) => { e.preventDefault(); setShowTerms(!showTerms); }}>{showTerms ? '접기' : '전문 보기'}</Button></Typography>} />
            <Collapse in={showTerms}>
              <Box component="ol" sx={{ m: 0, pl: 2.5, fontSize: 12.5, lineHeight: 1.7, color: 'text.secondary' }}>
                {(settings.terms || []).map((t) => <li key={t}>{t}</li>)}
              </Box>
            </Collapse>
          </>
        )}
        {(error || (calc.lines.length > 0 && calc.problems.length > 0)) && (
          <Alert severity={error ? 'error' : 'warning'} sx={{ mt: 1.5, whiteSpace: 'pre-line' }}>{error || calc.problems.join('\n')}</Alert>
        )}
        <Button fullWidth size="large" variant="contained" sx={{ mt: 2, minHeight: 48 }} onClick={submit} disabled={busy}>
          {busy ? '저장 중…' : mode === 'church' ? '신청하기' : '예약 추가 (입금 대기로 등록)'}
        </Button>
      </Paper>
    </Box>
  );
};

export default RequestForm;
